import { useEffect } from "react";
// App.jsx
import "@shoelace-style/shoelace/dist/themes/light.css";
import { setBasePath } from "@shoelace-style/shoelace/dist/utilities/base-path";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { useAppDispatch } from "./app/store";
import AxiosManager from "./components/AxiosManager";
import { PermissionRoute } from "./components/PermissionRoute/PermissionRoute";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import PageLoader from "./components/reusable/PageLoader/PageLoader";
import Searchbar from "./components/Searchbar/Searchbar";
import Sidebar from "./components/Sidebar/Sidebar";
import { selectLoaderPage } from "./features/Dashboard/Slices/dashboardSlice";
import {
  selectAuthenticated,
  setToken,
  setUser,
} from "./features/Dashboard/Slices/userSlice";
import { AccesoDenegado } from "./pages/AccesoDenegado/AccesoDenegado";
import Alumnos from "./pages/Alumnos/Alumnos";
import Colas from "./pages/Colas/Colas";
import Cupos from "./pages/Cupos/Cupos";
import Cursos from "./pages/Cursos/Cursos";
import EstadoGeneral from "./pages/EstadoGeneral/EstadoGeneral";
import Log from "./pages/Eventos/Eventos";
import EventosInscripcion from "./pages/EventosInscripcion/EventosInscripcion";
import Home from "./pages/Home/Home";
import InitialPage from "./pages/InitialPage/InitialPage";
import Login from "./pages/Login/Login";
import Novedades from "./pages/Novedades/Novedades";
import NovedadesMasivas from "./pages/NovedadesMasivas/NovedadesMasivas";
import NovedadesProgramadas from "./pages/NovedadesProgramadas/NovedadesProgramadas";
import Parametros from "./pages/Parametros/Parametros";
import Reservas from "./pages/Reservas/Reservas";
import Servicios from "./pages/Servicios/Servicios";
import Transacciones from "./pages/Transacciones/Transacciones";
import Validaciones from "./pages/Validaciones/Validaciones";
import "./variables.css";
import InscripcionesEnTransito from "./pages/InscripcionesEnTransito/InscripcionesEnTransito";
import DesinscripcionesEnTransito from "./pages/DesinscripcionesEnTransito.tsx/DesinscripcionesEnTransito";
import Cron from "./pages/Cron/Cron";

setBasePath(
  "https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.15.0/cdn/"
);

interface tknPayload {
  role: string;
  email: string;
}

function App() {
  const authenticated = useSelector(selectAuthenticated);
  const pageLoader = useSelector(selectLoaderPage);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const tkn = localStorage.getItem("token");
    if (tkn) {
      dispatch(setToken(tkn));
    }

    const decodePayload = async () => {
      if (!tkn) {
        return;
      }
      const decodedInfo: tknPayload = await jwtDecode(tkn);

      if (!decodedInfo.role) {
        return;
      }
      const user = {
        rol: decodedInfo.role,
        usuario: decodedInfo.email,
        acciones: [],
      };

      dispatch(setUser(user));
    };
    decodePayload();
  }, []);

  return (
    <BrowserRouter>
      <div className="flex flex-row flex-1 h-full">
        <ToastContainer />
        {authenticated && <Sidebar />}
        <div
          className={
            authenticated ? "pageContent bg-[#FAFCFE]" : "main-container"
          }
        >
          {pageLoader && <PageLoader />}
          {authenticated && <Searchbar />}
          <AxiosManager />
          <div>
            <Routes>
              <Route path="/" element={<InitialPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/sin-permisos" element={<AccesoDenegado />} />

              <Route
                element={<ProtectedRoute isAuthenticated={authenticated} />}
              >
                <Route
                  path="/home"
                  element={
                    <PermissionRoute module="home">
                      <Home />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/estado_general"
                  element={
                    <PermissionRoute module="estado_gral">
                      <EstadoGeneral />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/cron"
                  element={
                    <PermissionRoute module="estado_gral">
                      <Cron />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/cupos"
                  element={
                    <PermissionRoute module="cupo">
                      <Cupos />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/colas"
                  element={
                    <PermissionRoute module="colas">
                      <Colas />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/parametros"
                  element={
                    <PermissionRoute module="parametros">
                      <Parametros />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/alumnos"
                  element={
                    <PermissionRoute module="alumnos">
                      <Alumnos />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/cursos"
                  element={
                    <PermissionRoute module="cursos">
                      <Cursos />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/validaciones"
                  element={
                    <PermissionRoute module="validaciones">
                      <Validaciones />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/services"
                  element={
                    <PermissionRoute module="estado_de_servicio">
                      <Servicios />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/novedades"
                  element={
                    <PermissionRoute module="novedades">
                      <Novedades />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/novedades-masivas"
                  element={
                    <PermissionRoute module="novedades">
                      <NovedadesMasivas />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/novedades-programadas"
                  element={
                    <PermissionRoute module="novedades">
                      <NovedadesProgramadas />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/reservas"
                  element={
                    <PermissionRoute module="transacciones_fallidas">
                      <Reservas />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/inscripciones"
                  element={
                    <PermissionRoute module="transacciones_fallidas">
                      <InscripcionesEnTransito />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/desinscripciones"
                  element={
                    <PermissionRoute module="transacciones_fallidas">
                      <DesinscripcionesEnTransito />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/log"
                  element={
                    <PermissionRoute module="eventos">
                      <Log />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/eventos"
                  element={
                    <PermissionRoute module="eventos">
                      <EventosInscripcion />
                    </PermissionRoute>
                  }
                />

                <Route
                  path="/transacciones"
                  element={
                    <PermissionRoute module="transacciones">
                      <Transacciones />
                    </PermissionRoute>
                  }
                />
              </Route>
            </Routes>
          </div>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
